import { CheckedIcon, UnCheckedIcon } from '@components/icon/svg';
import { Icon } from '@iconify/react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  ListItemText,
  OutlinedInput,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatInTitleCase } from '@utils/constants';
import * as React from 'react';

const ITEM_HEIGHT = 90;
const ITEM_PADDING_TOP = 8;
const VISIBLE_ITEMS = 5;

const MenuProps = {
  PaperProps: {
    sx: {
      maxHeight: ITEM_HEIGHT * VISIBLE_ITEMS + ITEM_PADDING_TOP,
      overflow: 'hidden',
    },
  },
  MenuListProps: {
    sx: {
      maxHeight: ITEM_HEIGHT * VISIBLE_ITEMS + ITEM_PADDING_TOP,
      overflowY: 'auto',
      pb: '10px',
    },
  },
};

export default function MultiSelectTagFilter({
  label = 'Status',
  options = [],
  onFilterChange,
  searchControl = false,
  searchPlaceholder = 'Search Tag',
  selectedValue = [],
  isFiltering,
  isParticipantTable,
  isFetching,
  searchQuery,
  setSearchQuery,
}) {
  const [open, setOpen] = React.useState(false);
  const didMount = React.useRef(false);
  const initialMap = React.useMemo(() => {
    const m = {};
    selectedValue.forEach(({ tag_key, value_id }) => {
      m[tag_key] = Array.isArray(value_id) ? value_id : [];
    });
    return m;
  }, [selectedValue]);

  const [selectedValuesByTag, setSelectedValuesByTag] = React.useState(initialMap);

  const totalSelectedCount = React.useMemo(
    () => Object.values(selectedValuesByTag).reduce((sum, arr) => sum + (arr?.length || 0), 0),
    [selectedValuesByTag],
  );
  const handleApply = () => {
    const result = Object.entries(selectedValuesByTag)
      .filter(([, vals]) => vals.length > 0)
      .map(([tag_key, value_id]) => ({ tag_key_id: tag_key, value_key_id: value_id }));
    onFilterChange(result);
  };

  React.useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (isFiltering === false) {
      setOpen(false);
    }
  }, [isFiltering]);

  const handleClearAll = React.useCallback(
    (e) => {
      e.stopPropagation();
      const resetMap = options.reduce((acc, opt) => {
        acc[opt.tag_key_id] = [];
        return acc;
      }, {});
      setSelectedValuesByTag(resetMap);
      onFilterChange([]);
      setOpen(false);
    },
    [options, onFilterChange, selectedValuesByTag],
  );

  return (
    <>
      {isParticipantTable ? (
        <FormControl>
          <Select
            multiple
            displayEmpty
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            value={[]}
            input={<OutlinedInput />}
            renderValue={() => {
              if (totalSelectedCount === 0) {
                return <b>{label}</b>;
              }

              return (
                <Box display={'flex'} alignItems={'center'}>
                  <Typography display="flex" alignItems="center" gap={1} fontWeight={'bold'}>
                    {totalSelectedCount > 0 ? `${label} (${totalSelectedCount})` : label}
                  </Typography>
                  <Tooltip title="Clear Selection" arrow>
                    <CloseIcon
                      fontSize="small"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={handleClearAll}
                      sx={{ cursor: 'pointer', ml: 1, '&:hover': { color: 'error.main' } }}
                    />
                  </Tooltip>
                </Box>
              );
            }}
            MenuProps={MenuProps}
          >
            {searchControl && (
              <Box px={1} py={1} onClick={(e) => e.stopPropagation()}>
                <Box display={'flex'} justifyContent={'end'} mb={1} mr={0.3}>
                  <Typography color={'#7d5ae8'} sx={{ cursor: 'pointer' }} onClick={handleClearAll}>
                    Clear All
                  </Typography>
                </Box>
                <TextField
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchQuery(e.target.value);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  size="small"
                  fullWidth
                />
              </Box>
            )}

            {isFetching ? (
              <Box display={'flex'} justifyContent={'center'} my={3}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              (!options || options.length <= 0) && (
                <Typography textAlign="center" p={1}>
                  Empty tag
                </Typography>
              )
            )}

            {options.map((option) => (
              <Box key={option.tag_key_id} sx={{ mb: 1 }}>
                {/* Tag header (select all / none) */}
                <Box display="flex" alignItems="center" ml={1}>
                  <Checkbox
                    size="small"
                    disabled
                    sx={{ p: 0, mr: 1 }}
                    indeterminate={
                      (selectedValuesByTag[option.tag_key_id]?.length || 0) > 0 &&
                      (selectedValuesByTag[option.tag_key_id]?.length || 0) <
                        option.tag_values.length
                    }
                    checked={
                      (selectedValuesByTag[option.tag_key_id]?.length || 0) ===
                      option.tag_values.length
                    }
                    onChange={() => {
                      setSelectedValuesByTag((prev) => {
                        const allIds = option.tag_values.map((v) => v.value_id);
                        const currently = prev[option.tag_key_id] || [];
                        const next = currently.length === allIds.length ? [] : allIds;
                        return { ...prev, [option.tag_key_id]: next };
                      });
                    }}
                    checkedIcon={<CheckedIcon />}
                    icon={<UnCheckedIcon />}
                  />
                  <ListItemText primary={formatInTitleCase(option.tag_key)} />
                </Box>

                {/* Values list */}
                <Box sx={{ bgcolor: '#fbfbfb', mx: 1, borderRadius: 1, p: 1 }}>
                  {option.tag_values.map((item) => (
                    <Box
                      key={item.tag_value_id}
                      sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}
                    >
                      <Checkbox
                        size="small"
                        sx={{ p: 0, mr: 1 }}
                        checked={selectedValuesByTag[option.tag_key_id]?.includes(
                          item.tag_value_id,
                        )}
                        onChange={() => {
                          setSelectedValuesByTag((prev) => {
                            const curr = prev[option.tag_key_id] || [];
                            const next = curr.includes(item.tag_value_id)
                              ? curr.filter((v) => v !== item.tag_value_id)
                              : [...curr, item.tag_value_id];
                            return { ...prev, [option.tag_key_id]: next };
                          });
                        }}
                        checkedIcon={<CheckedIcon />}
                        icon={<UnCheckedIcon />}
                      />
                      <Typography variant="body2">{formatInTitleCase(item.value_key)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}

            {/* Sticky footer */}
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                width: '100%',
                bgcolor: 'background.paper',
                borderTop: 1,
                borderColor: 'divider',
                p: 1,
                zIndex: 1,
              }}
            >
              <Button variant="contained" fullWidth onClick={handleApply} disabled={isFiltering}>
                {isFiltering ? <CircularProgress size={24} /> : 'Apply Filter'}
              </Button>
            </Box>
          </Select>
        </FormControl>
      ) : (
        <FormControl fullWidth>
          <Select
            multiple
            displayEmpty
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            size="small"
            value={[]}
            input={<OutlinedInput />}
            renderValue={() => {
              if (totalSelectedCount === 0) {
                return (
                  <Typography fontWeight={'bold'} display={'flex'} alignItems={'center'}>
                    <Icon icon="mdi:filter-outline" width="22" height="22" />
                    {label}
                  </Typography>
                );
              }

              return (
                <Box display={'flex'} alignItems={'center'}>
                  <Typography display="flex" alignItems="center" fontWeight={'bold'}>
                    <Icon icon="mdi:filter-outline" width="22" height="22" />{' '}
                    {totalSelectedCount > 0 ? `${label} (${totalSelectedCount})` : 'madan'}
                  </Typography>
                  <Tooltip title="Clear Selection" arrow>
                    <CloseIcon
                      fontSize="small"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={handleClearAll}
                      sx={{ cursor: 'pointer', ml: 1, '&:hover': { color: 'error.main' } }}
                    />
                  </Tooltip>
                </Box>
              );
            }}
            MenuProps={MenuProps}
          >
            {searchControl && (
              <Box px={1} py={1} onClick={(e) => e.stopPropagation()}>
                <Box display={'flex'} justifyContent={'end'} mb={1} mr={0.3}>
                  <Typography color={'#7d5ae8'} sx={{ cursor: 'pointer' }} onClick={handleClearAll}>
                    Clear All
                  </Typography>
                </Box>
                <TextField
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchQuery(e.target.value);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  size="small"
                  fullWidth
                />
              </Box>
            )}

            {isFetching ? (
              <Box display={'flex'} justifyContent={'center'} my={3}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              (!options || options.length <= 0) && (
                <Typography textAlign="center" p={1}>
                  Empty tag
                </Typography>
              )
            )}

            {options.map((option) => (
              <Box key={option.tag_key_id} sx={{ mb: 1 }}>
                <Box display="flex" alignItems="center" ml={1}>
                  <Checkbox
                    size="small"
                    sx={{ p: 0, mr: 1 }}
                    indeterminate={
                      (selectedValuesByTag[option.tag_key_id]?.length || 0) > 0 &&
                      (selectedValuesByTag[option.tag_key_id]?.length || 0) <
                        option.tag_values.length
                    }
                    disabled
                    checked={
                      (selectedValuesByTag[option.tag_key_id]?.length || 0) ===
                      option.tag_values.length
                    }
                    onChange={() => {
                      setSelectedValuesByTag((prev) => {
                        const allIds = option.tag_values.map((v) => v.value_id);
                        const currently = prev[option.tag_key_id] || [];
                        const next = currently.length === allIds.length ? [] : allIds;
                        return { ...prev, [option.tag_key_id]: next };
                      });
                    }}
                    checkedIcon={<CheckedIcon />}
                    icon={<UnCheckedIcon />}
                  />
                  <ListItemText primary={formatInTitleCase(option.tag_key)} />
                </Box>

                {/* Values list */}
                <Box sx={{ bgcolor: '#fbfbfb', mx: 1, borderRadius: 1, p: 1 }}>
                  {option.tag_values.map((item) => (
                    <Box
                      key={item.tag_value_id}
                      sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}
                    >
                      <Checkbox
                        size="small"
                        sx={{ p: 0, mr: 1 }}
                        checked={selectedValuesByTag[option.tag_key_id]?.includes(
                          item.tag_value_id,
                        )}
                        onChange={() => {
                          setSelectedValuesByTag((prev) => {
                            const curr = prev[option.tag_key_id] || [];
                            const next = curr.includes(item.tag_value_id)
                              ? curr.filter((v) => v !== item.tag_value_id)
                              : [...curr, item.tag_value_id];
                            return { ...prev, [option.tag_key_id]: next };
                          });
                        }}
                        checkedIcon={<CheckedIcon />}
                        icon={<UnCheckedIcon />}
                      />
                      <Typography variant="body2">{formatInTitleCase(item.value_key)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}

            {/* Sticky footer */}
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                width: '100%',
                bgcolor: 'background.paper',
                borderTop: 1,
                borderColor: 'divider',
                p: 1,
                zIndex: 1,
              }}
            >
              <Button variant="contained" fullWidth onClick={handleApply} disabled={isFiltering}>
                {isFiltering ? <CircularProgress size={24} /> : 'Apply Filter'}
              </Button>
            </Box>
          </Select>
        </FormControl>
      )}
    </>
  );
}
